import { createContext, useContext, useReducer, useCallback, useMemo, useEffect } from 'react';

import { COMPLETED_NEXT_UP_CARD } from '#client/constants/nextUpCards';
import { useLogError } from '#client/hooks/useLogError';

import { useBankingDataContext } from '../BankingDataContext';

export const NextUpCardsContext = createContext();
NextUpCardsContext.displayName = 'NextUpCardsContext';

const useNextUpCardsContext = () => {
  const context = useContext(NextUpCardsContext);

  if (typeof context === 'undefined') {
    throw new Error('useNextUpCardsContext must be used within a NextUpCardsContext.Provider');
  }

  return context;
};

const reducer = (prevState, action) => {
  switch (action.type) {
    case 'UPDATE_DATA':
      return action.payload;
    case 'REMOVE_CARD':
      if (!prevState.nextUpCards.find(({ type }) => type === action.payload)) {
        return prevState;
      }

      return {
        ...prevState,
        nextUpCards: prevState.nextUpCards.filter(({ type }) => type !== action.payload),
        activeStep: 0
      };
    case 'REMOVING_CARD': {
      const nextState = prevState.nextUpCards.map(
        (card) =>
          card.type === action.payload
            ? { ...card, removing: true } // Apply changes to the matching card
            : card // Return the card unchanged if it doesn't match
      );

      return {
        ...prevState,
        nextUpCards: nextState
      };
    }

    case 'ADD_CARD':
      if (prevState.nextUpCards.find(({ type }) => type === action.payload.type)) {
        return prevState;
      }

      return {
        ...prevState,
        // order the nextUpCards by order from 1 to n
        nextUpCards: [...prevState.nextUpCards, action.payload].sort((a, b) => a.order - b.order)
      };
    case 'SET_ACTIVE_STEP':
      return {
        ...prevState,
        activeStep: action.payload
      };
    default:
      return prevState;
  }
};

export function NextUpCardsProvider({ children }) {
  const [{ nextUpCards, activeStep }, dispatch] = useReducer(reducer, {
    nextUpCards: [],
    activeStep: 0
  });

  const {
    error,
    errors,
    data: initData,
    loading,
    refetch: refetchNextUpCards
  } = useBankingDataContext();

  useLogError(errors, error);

  const setActiveStep = useCallback((step) => {
    dispatch({
      type: 'SET_ACTIVE_STEP',
      payload: step
    });
  }, []);

  /**
   * This function adds a card to the nextUpCards array
   * @param {string} type The type of the card to add
   * @param {number} order The order of the card to add
   */
  const addCard = useCallback(({ type, order }) => {
    dispatch({
      type: 'ADD_CARD',
      payload: {
        type,
        order
      }
    });
  }, []);

  /**
   * This function checks if the completed card should be shown
   * @param {string} lastCard The type of the last card removed
   */
  const checkShowCompletedCard = useCallback(
    (lastCard) => {
      if (nextUpCards.length === 1 && lastCard !== COMPLETED_NEXT_UP_CARD) {
        // completed card should be shown which order larger than the last card
        const ORDER_COMPLETED_CARD = 999;
        addCard({ type: COMPLETED_NEXT_UP_CARD, order: ORDER_COMPLETED_CARD });
      }
    },
    [addCard, nextUpCards.length]
  );

  /**
   * This function removes a card from the nextUpCards array
   * @param {string} type The type of the card to remove
   */
  const removeCard = useCallback(
    (type) => {
      // check if type in nextUpCards
      if (!nextUpCards.find((card) => card.type === type)) {
        return;
      }

      dispatch({
        type: 'REMOVE_CARD',
        payload: type
      });

      checkShowCompletedCard(type);
    },
    [checkShowCompletedCard, nextUpCards]
  );

  /**
   * This function removes a card from the nextUpCards array with an animation delay
   * @param {string} type The type of the card to remove
   */
  const removeCardWithAnimation = useCallback(
    (type, callback, options) => {
      // check if type in nextUpCards
      if (!nextUpCards.find((card) => card.type === type)) {
        return;
      }

      const { isCheckCompleted } = options || {
        // this option is used to check if the completed card should be shown
        isCheckCompleted: true
      };

      setTimeout(() => {
        dispatch({
          type: 'REMOVING_CARD',
          payload: type
        });
      }, 500);

      setTimeout(() => {
        dispatch({
          type: 'REMOVE_CARD',
          payload: type
        });

        if (typeof callback === 'function') {
          callback();
        }

        if (isCheckCompleted) {
          checkShowCompletedCard(type);
        }
      }, 2500);
    },
    [checkShowCompletedCard, nextUpCards]
  );

  useEffect(() => {
    if (initData) {
      dispatch({
        type: 'UPDATE_DATA',
        payload: {
          nextUpCards: initData?.nextUpCards?.nextUpCards || [],
          activeStep: 0
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [!!initData]);

  const value = useMemo(
    () => ({
      error,
      loading,
      nextUpCards,
      activeStep,
      setActiveStep,
      addCard,
      removeCard,
      removeCardWithAnimation,
      refetchNextUpCards
    }),
    [
      error,
      loading,
      nextUpCards,
      activeStep,
      setActiveStep,
      addCard,
      removeCard,
      removeCardWithAnimation,
      refetchNextUpCards
    ]
  );

  return <NextUpCardsContext.Provider value={value}>{children}</NextUpCardsContext.Provider>;
}

export default useNextUpCardsContext;
